import React, { useState } from 'react';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card, Badge } from '@/components/ui';
import { useMaintenanceWorkloads } from './hooks/useMaintenanceWorkload';
import { WorkloadDesktopTable } from './components/WorkloadDesktopTable';
import { WorkloadFormModal } from './components/WorkloadFormModal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useProjects } from '@/modules/projects/shared/hooks/useProjects';
import { useZones } from '@/modules/maintenance/zones/hooks/useMaintenanceZones';
import type { MaintenanceWorkload } from './types/maintenance-workload.types';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  calculated: 'bg-blue-100 text-blue-600',
  reviewing: 'bg-amber-100 text-amber-600',
  approved: 'bg-emerald-100 text-emerald-600',
  planned: 'bg-os-primary/10 text-os-primary',
  archived: 'bg-slate-200 text-slate-500',
};

const statusLabels: Record<string, string> = {
  draft: 'Bản nháp',
  calculated: 'Đã tính toán',
  reviewing: 'Đang soát xét',
  approved: 'Đã duyệt',
  planned: 'Đã lập kế hoạch',
  archived: 'Lưu trữ',
};

export default function MaintenanceWorkloadPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');
  const [showCreate, setShowCreate] = useState(false);
  const [editWorkload, setEditWorkload] = useState<MaintenanceWorkload | null>(null);
  const [deleteWorkload, setDeleteWorkload] = useState<MaintenanceWorkload | null>(null);

  const { projects = [] } = useProjects(companyId);
  const { zones = [] } = useZones(companyId);

  const {
    workloads,
    total,
    stats,
    isLoading,
    filters,
    setFilters,
    deleteWorkload: executeDelete,
    isDeleting,
    calculateWorkload,
    isCalculating
  } = useMaintenanceWorkloads(companyId);

  const handleSearchChange = (value: string) => {
    setFilters({ ...filters, search: value, page: 1 });
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const handleDelete = async () => {
    if (!deleteWorkload) return;
    try {
      await executeDelete(deleteWorkload.id);
      setDeleteWorkload(null);
      toast.success('Đã xóa workload thành công');
    } catch (error: any) {
      toast.error('Lỗi khi xóa: ' + error.message);
    }
  };

  const handleCalculate = async (id: string) => {
    try {
      await calculateWorkload(id);
      toast.success('Đã tính toán lại khối lượng');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  return (
    <PageLayout
      title="Khối lượng bảo dưỡng (Workload)"
      description="Tính toán khối lượng chăm sóc cây xanh, thảm cỏ, nhân công, vật tư theo từng khu vực."
      breadcrumbs={[{ label: 'Bảo trì & Bảo dưỡng' }, { label: 'Khối lượng' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-file-list-3-line">Quản lý Định mức</Button>
          <AuthButton
            module="maintenance"
            action="create"
            variant="primary"
            icon="ri-add-line"
            onClick={() => setShowCreate(true)}
          >
            Khởi tạo Workload
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI Board */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Tổng giờ công (Man-hours)"
            value={stats?.totalManHours || 0}
            icon="ri-user-settings-line"
            variant="info"
          />
          <KPICard
            title="Vật tư dự kiến"
            value={(stats?.totalMaterialCost || 0).toLocaleString() + ' đ'}
            icon="ri-money-dollar-circle-line"
            variant="success"
          />
          <KPICard
            title="Điểm tải trung bình"
            value={stats?.avgScore?.toFixed(1) || 0}
            icon="ri-dashboard-3-line"
            variant="warning"
          />
          <KPICard
            title="Workload đang duyệt"
            value={stats?.byStatus?.reviewing || 0}
            icon="ri-time-line"
            variant="info"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-col md:flex-row gap-4 w-full justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-3 flex-1">
              <Search
                placeholder="Tìm tiêu đề, mô tả workload..."
                value={filters.search}
                onChange={handleSearchChange}
                className="w-full md:w-80"
              />
              <Filter
                options={[
                  {
                    id: 'projectId', label: 'Dự án', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả dự án' },
                      ...projects.map(p => ({ value: p.id, label: p.name }))
                    ]
                  },
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      ...Object.entries(statusLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  }
                ]}
                onFilterChange={handleFilterChange}
              />
            </div>
            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl self-end md:self-auto">
              <Button
                variant={viewMode === 'card' ? 'primary' : 'ghost'}
                size="sm"
                className="rounded-lg px-3"
                onClick={() => setViewMode('card')}
              >
                <i className="ri-layout-grid-line mr-1" /> Thẻ
              </Button>
              <Button
                variant={viewMode === 'table' ? 'primary' : 'ghost'}
                size="sm"
                className="rounded-lg px-3"
                onClick={() => setViewMode('table')}
              >
                <i className="ri-table-line mr-1" /> Bảng
              </Button>
            </div>
          </div>
        </PageLayout.Toolbar>

        {viewMode === 'card' ? (
          <div className="grid grid-cols-1 gap-4">
            {workloads.map((wl) => (
              <Card key={wl.id} className="p-5 hover:border-os-primary/50 transition-all cursor-pointer group" onClick={() => navigate(`./detail/${wl.id}`)}>
                <div className="flex flex-col md:flex-row justify-between gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge className={`${statusColors[wl.status]} border-transparent text-[10px] font-bold `}>
                        {statusLabels[wl.status]}
                      </Badge>
                      <h3 className="font-bold text-slate-900 group-hover:text-os-primary transition-colors">{wl.title}</h3>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-building-line" /> {wl.project?.name}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-map-pin-2-line" /> {wl.zone?.zone_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line" /> {new Date(wl.period_start).toLocaleDateString('vi-VN')} - {new Date(wl.period_end).toLocaleDateString('vi-VN')}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-[10px] text-slate-400 font-bold ">Man-hours</p>
                      <p className="font-bold text-slate-900">{wl.estimated_man_hours?.toLocaleString() || 0}h</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-slate-400 font-bold ">Material</p>
                      <p className="font-bold text-slate-900">{((wl.estimated_material_cost || 0) / 1000000).toFixed(1)}M</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-slate-400 font-bold ">Score</p>
                      <div className="flex items-center gap-1">
                        <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${wl.total_workload_score > 80 ? 'bg-red-500' : wl.total_workload_score > 50 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                            style={{ width: `${wl.total_workload_score}%` }}
                          />
                        </div>
                        <span className="font-bold text-slate-900 text-xs">{wl.total_workload_score}</span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" icon="ri-calculator-line" onClick={(e) => { e.stopPropagation(); handleCalculate(wl.id); }} isLoading={isCalculating} />
                      <Button variant="ghost" size="sm" icon="ri-edit-line" onClick={(e) => { e.stopPropagation(); setEditWorkload(wl); }} />
                      <Button variant="ghost" size="sm" icon="ri-delete-bin-line" className="text-red-400 hover:text-red-600" onClick={(e) => { e.stopPropagation(); setDeleteWorkload(wl); }} />
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="overflow-hidden border-none shadow-sm">
            <WorkloadDesktopTable
              data={workloads}
              isLoading={isLoading}
              onEdit={setEditWorkload}
              onDelete={setDeleteWorkload}
              onRowClick={(wl) => navigate(`./detail/${wl.id}`)}
            />
          </Card>
        )}

        {workloads.length === 0 && !isLoading && (
          <div className="text-center py-20 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
            <i className="ri-calculator-line text-5xl text-slate-300 mb-3" />
            <h3 className="text-lg font-bold text-slate-900">Chưa có dữ liệu Workload</h3>
            <p className="text-slate-500 text-sm max-w-md mx-auto mt-1">Khởi tạo workload đầu tiên để bắt đầu tính toán khối lượng bảo dưỡng, nhân công và vật tư.</p>
            <Button variant="primary" icon="ri-add-line" className="mt-6" onClick={() => setShowCreate(true)}>Khởi tạo ngay</Button>
          </div>
        )}
      </div>

      <WorkloadFormModal
        isOpen={showCreate || !!editWorkload}
        workload={editWorkload}
        companyId={companyId}
        onClose={() => {
          setShowCreate(false);
          setEditWorkload(null);
        }}
      />

      <ConfirmDialog
        isOpen={!!deleteWorkload}
        title="Xác nhận xóa Workload"
        message={`Bạn có chắc chắn muốn xóa "${deleteWorkload?.title}"? Dữ liệu tính toán khối lượng chi tiết sẽ bị mất.`}
        confirmText="Xác nhận xóa"
        isDestructive={true}
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteWorkload(null)}
      />
    </PageLayout>
  );
}
